import { NgZone } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { filter, take } from 'rxjs'
import { afterIdle } from './app/shared/utils/after-idle'
import { bootstrapApplication } from '@angular/platform-browser'
import { addIntegration, browserTracingIntegration, getClient, init, lazyLoadIntegration } from '@sentry/angular'
import { AppComponent } from './app/app.component'
import { appConfig } from './app/app.config'
import { environment } from './environments/environment'
import { isChunkLoadError } from './app/shared/handlers/chunk-load-error'
import { IGNORED_POST_MESSAGE_ERROR, isIgnoredError } from './app/shared/handlers/ignored-error'

declare global {
  interface Window {
    dataLayer: any[]
  }
}

if (environment.production) {
  init({
    dsn: 'https://20b588145ee16ea20257a7d6ac972ee2@o4507911733313536.ingest.de.sentry.io/4508460858671184',
    integrations: [browserTracingIntegration()],
    ignoreErrors: [IGNORED_POST_MESSAGE_ERROR],
    // Tracing
    tracesSampleRate: 0.3,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', 'https://dashboard.taketheme.com', 'https://api.taketheme.com'],
    // Session Replay
    replaysSessionSampleRate: 0.01, // This sets the sample rate at 1%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0.3, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    beforeSend(event, hint) {
      const error = hint?.originalException as any
      if (isIgnoredError(error) || isChunkLoadError(error)) return null
      if (event.exception?.values?.some(exception => isChunkLoadError(exception.value) || isChunkLoadError(exception.type))) return null
      if (error?.status === 402 || error?.message?.includes('402')) {
        return null
      }
      return event
    },
  })
}

bootstrapApplication(AppComponent, appConfig)
  .then(app => {
    if (!environment.production) return
    // Error reporting starts above; wait for the first routed page before replay.
    const start = () =>
      app.injector.get(NgZone).runOutsideAngular(() =>
        afterIdle(async () => {
          const client = getClient()
          if (!client || client.getIntegrationByName('Replay')) return
          // The SDK loader fetches its own versioned recorder. A bundled import
          // through Angular's browser re-exports otherwise stays in an eager chunk.
          const replay = await lazyLoadIntegration('replayIntegration')
          addIntegration(replay())
        }),
      )
    const router = app.injector.get(Router)
    if (router.navigated) start()
    else
      router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          take(1),
        )
        .subscribe(start)
  })
  .catch(err => console.error(err))
